(() => {
  const ROOT_SELECTOR = '[data-ecsv]';

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  function initSocialVideos(root) {
    if (!root || root.dataset.ecsvReady === 'true') return;
    root.dataset.ecsvReady = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-ecsv-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-ecsv-panel]'));
    const autoplay = root.dataset.autoplay === 'true';
    const muted = root.dataset.muted !== 'false';
    const sliders = new Map();
    let sectionVisible = true;

    const pausePanel = (panel) => {
      panel.querySelectorAll('video').forEach((video) => {
        try {
          video.pause();
        } catch (_error) {}
      });
    };

    const syncVideos = (panel) => {
      if (!panel) return;
      const isActive = panel.classList.contains('is-active') && sectionVisible;
      const viewport = panel.querySelector('[data-ecsv-viewport]');
      const viewportRect = viewport ? viewport.getBoundingClientRect() : null;

      panel.querySelectorAll('video').forEach((video) => {
        video.muted = muted;
        const card = video.closest('[data-ecsv-card]');
        const cardRect = card ? card.getBoundingClientRect() : null;
        const nearViewport = !viewportRect || !cardRect || (
          cardRect.right > viewportRect.left - 80 &&
          cardRect.left < viewportRect.right + 80
        );

        if (!autoplay || !isActive || !nearViewport) {
          try {
            video.pause();
          } catch (_error) {}
          return;
        }

        const promise = video.play();
        if (promise && typeof promise.catch === 'function') promise.catch(() => {});
      });
    };

    const activate = (key, focusTab = false) => {
      tabs.forEach((tab) => {
        const selected = tab.dataset.ecsvTab === key;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focusTab) tab.focus({ preventScroll: true });
      });

      panels.forEach((panel) => {
        const selected = panel.dataset.ecsvPanel === key;
        panel.classList.toggle('is-active', selected);
        panel.hidden = !selected;
        if (selected) {
          const slider = sliders.get(key);
          if (slider) {
            slider.viewport.scrollTo({ left: 0, behavior: 'auto' });
            slider.update();
          }
          syncVideos(panel);
        } else {
          pausePanel(panel);
        }
      });
    };

    panels.forEach((panel) => {
      const viewport = panel.querySelector('[data-ecsv-viewport]');
      const cards = Array.from(panel.querySelectorAll('[data-ecsv-card]'));
      const prev = panel.querySelector('[data-ecsv-prev]');
      const next = panel.querySelector('[data-ecsv-next]');
      if (!viewport || !cards.length) return;

      let frame = 0;
      let dragStartX = 0;
      let dragStartScroll = 0;
      let dragging = false;
      let pointerId = null;
      let cancelLinkUntil = 0;

      const maxScroll = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const nearestIndex = () => {
        let bestIndex = 0;
        let bestDistance = Infinity;
        cards.forEach((card, index) => {
          const distance = Math.abs(card.offsetLeft - viewport.scrollLeft);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
          }
        });
        return bestIndex;
      };

      const update = () => {
        const maximum = maxScroll();
        if (prev) prev.disabled = viewport.scrollLeft <= 2;
        if (next) next.disabled = viewport.scrollLeft >= maximum - 2;
        syncVideos(panel);
      };

      const scheduleUpdate = () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(update);
      };

      const scrollToIndex = (index, smooth = true) => {
        const target = cards[clamp(index, 0, cards.length - 1)];
        if (!target) return;
        viewport.scrollTo({
          left: clamp(target.offsetLeft, 0, maxScroll()),
          behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto'
        });
      };

      prev?.addEventListener('click', () => scrollToIndex(nearestIndex() - 1));
      next?.addEventListener('click', () => scrollToIndex(nearestIndex() + 1));
      viewport.addEventListener('scroll', scheduleUpdate, { passive: true });
      viewport.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        scrollToIndex(nearestIndex() + (event.key === 'ArrowRight' ? 1 : -1));
      });

      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        pointerId = event.pointerId;
        dragStartX = event.clientX;
        dragStartScroll = viewport.scrollLeft;
        dragging = false;
        viewport.setPointerCapture(pointerId);
      });

      viewport.addEventListener('pointermove', (event) => {
        if (pointerId !== event.pointerId) return;
        const delta = event.clientX - dragStartX;
        if (!dragging && Math.abs(delta) > 5) {
          dragging = true;
          viewport.classList.add('is-dragging');
        }
        if (!dragging) return;
        event.preventDefault();
        viewport.scrollLeft = dragStartScroll - delta;
      });

      const endDrag = (event) => {
        if (pointerId !== event.pointerId) return;
        if (viewport.hasPointerCapture(pointerId)) viewport.releasePointerCapture(pointerId);
        pointerId = null;
        if (dragging) {
          cancelLinkUntil = Date.now() + 350;
          viewport.classList.remove('is-dragging');
          scrollToIndex(nearestIndex());
        }
        dragging = false;
      };

      viewport.addEventListener('pointerup', endDrag);
      viewport.addEventListener('pointercancel', endDrag);
      viewport.addEventListener('click', (event) => {
        if (Date.now() >= cancelLinkUntil) return;
        if (event.target.closest('a')) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);

      const resizeObserver = 'ResizeObserver' in window
        ? new ResizeObserver(() => {
          const index = nearestIndex();
          window.requestAnimationFrame(() => {
            scrollToIndex(index, false);
            update();
          });
        })
        : null;
      resizeObserver?.observe(viewport);

      sliders.set(panel.dataset.ecsvPanel, { viewport, cards, update, scrollToIndex });
      update();
    });

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab.dataset.ecsvTab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        activate(tabs[nextIndex].dataset.ecsvTab, true);
      });
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          sectionVisible = entry.isIntersecting;
          root.classList.toggle('is-visible', entry.isIntersecting);
          const activePanel = root.querySelector('[data-ecsv-panel].is-active');
          if (entry.isIntersecting) syncVideos(activePanel);
          else panels.forEach(pausePanel);
        });
      }, { rootMargin: '120px 0px', threshold: 0.08 });
      observer.observe(root);
    } else {
      root.classList.add('is-visible');
    }

    document.addEventListener('shopify:block:select', (event) => {
      const card = root.querySelector(`[data-block-id="${event.detail.blockId}"]`);
      if (!card) return;
      const panel = card.closest('[data-ecsv-panel]');
      if (!panel) return;
      activate(panel.dataset.ecsvPanel);
      window.requestAnimationFrame(() => {
        const slider = sliders.get(panel.dataset.ecsvPanel);
        const index = slider?.cards.indexOf(card) ?? -1;
        if (index >= 0) slider.scrollToIndex(index, false);
      });
    });

    const initiallyActive = tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0];
    if (initiallyActive) activate(initiallyActive.dataset.ecsvTab);
  }

  function boot(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initSocialVideos(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initSocialVideos);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
